/**
 * Last known state of a collection, kept on the device.
 *
 * The cards screen used to fetch every card a user owns, `select('*')` with no
 * limit, and render nothing until all of it arrived. On a good connection that
 * is a beat. On cellular it was up to a minute of empty screen, and sometimes
 * the request never finished at all.
 *
 * A collection barely changes between two openings of the same app, so the
 * right thing to show while the network works is what was there last time.
 * This is that: paint from the cache immediately, replace it when the real
 * answer lands, write the new state back.
 *
 * Deliberately localStorage rather than a plugin. It is synchronous, so the
 * first paint does not wait on a promise, and the whole point is the first
 * paint.
 */

const KEY = (profileId) => `cc:cards:${profileId}`;
const META = (profileId) => `cc:cards:meta:${profileId}`;

/**
 * Stale but shown anyway. A price that moved or a card added on another device
 * is corrected within a second of the fetch landing, and a wrong card for one
 * second beats an empty screen for thirty.
 */
export function readCards(profileId) {
  if (!profileId) return null;
  try {
    const raw = localStorage.getItem(KEY(profileId));
    if (!raw) return null;
    const cards = JSON.parse(raw);
    return Array.isArray(cards) && cards.length ? cards : null;
  } catch {
    // Corrupt or unavailable storage is a cache miss, never an error.
    return null;
  }
}

export function readMeta(profileId) {
  if (!profileId) return null;
  try {
    return JSON.parse(localStorage.getItem(META(profileId)) || 'null');
  } catch {
    return null;
  }
}

/**
 * What is worth keeping on the device.
 *
 * A collection of 69 cards serialised to 6.8MB, against a storage limit of
 * about 5, so every write failed and the cache silently never existed. Two
 * columns were 3.3MB each: `digital_image_url` and `tcgdex_image` hold a
 * base64 data URI rather than a URL on most rows, so the whole image was being
 * stored twice per card.
 *
 * A data URI is dropped and the real row supplies it a moment later. Where the
 * column genuinely holds a URL it is short and it stays, so most thumbnails
 * still paint from the cache.
 */
const IMAGE_FIELDS = ['digital_image_url', 'tcgdex_image', 'original_image_url'];
/** Longer than any real URL, shorter than any inlined image. */
const URL_MAX = 512;

function slimForCache(card) {
  const out = { ...card };
  for (const field of IMAGE_FIELDS) {
    const v = out[field];
    if (typeof v === 'string' && (v.startsWith('data:') || v.length > URL_MAX)) {
      out[field] = null;
    }
  }
  // Diagnostic history for the scanner. Never read by the grid.
  delete out.scan_log;
  return out;
}

/**
 * Writes are best-effort and never throw.
 *
 * A big collection can exceed the storage quota, and a user whose cache cannot
 * be written should still be able to use the app. On quota failure the oldest
 * cards go first: the screen is sorted newest first, so the top of the list is
 * what someone actually sees.
 */
export function writeCards(profileId, cards) {
  if (!profileId || !Array.isArray(cards)) return;

  const slim = cards.map(slimForCache);

  const attempt = (slice) => {
    localStorage.setItem(KEY(profileId), JSON.stringify(slice));
    localStorage.setItem(META(profileId), JSON.stringify({
      at: Date.now(), count: slice.length, truncated: slice.length < cards.length,
    }));
  };

  // Halve until it fits rather than giving up after one retry at a size that
  // was never going to be different. The screen is newest first, so what
  // survives is what someone actually sees when they open it.
  let take = slim.length;
  for (let tries = 0; tries < 8 && take > 0; tries += 1) {
    try {
      attempt(slim.slice(0, take));
      return;
    } catch {
      take = Math.floor(take / 2);
    }
  }
  console.warn('[CardCache] could not store the collection; storage is full or blocked');
}

/**
 * Last known price per card, kept beside the cards.
 *
 * A value sort with no prices yet is not a value sort. On a cold open the
 * badges take a few seconds to arrive in waves, and until then a collection
 * sorted by value sat in name order and then reshuffled as each wave landed.
 * Yesterday's prices put the grid in the right order on the first paint; the
 * fresh ones replace them a moment later, usually without moving anything.
 */
const PRICES = (profileId) => `cc:prices:${profileId}`;

export function readPrices(profileId) {
  if (!profileId) return null;
  try {
    const parsed = JSON.parse(localStorage.getItem(PRICES(profileId)) || 'null');
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function writePrices(profileId, prices) {
  if (!profileId || !prices || typeof prices !== 'object') return;
  const slim = {};
  for (const [id, p] of Object.entries(prices)) {
    if (p && p.price != null) slim[id] = { price: p.price, formatted: p.formatted };
  }
  try { localStorage.setItem(PRICES(profileId), JSON.stringify(slim)); } catch { /* full or blocked */ }
}

export function clearCards(profileId) {
  if (!profileId) return;
  try {
    localStorage.removeItem(KEY(profileId));
    localStorage.removeItem(META(profileId));
    localStorage.removeItem(PRICES(profileId));
  } catch {
    /* nothing to do */
  }
}

/**
 * The first page is what the user sees; the rest is scrolling.
 *
 * Sized so it fills a phone screen several times over without being a payload
 * worth waiting for on a slow connection.
 */
export const FIRST_PAGE = 60;

/**
 * Which collection was last on screen.
 *
 * The cache is keyed by profile, and the profile is not known until a query
 * comes back - so a cache that can only be read after that query still leaves a
 * full-screen spinner in front of it. Remembering the last profile lets the
 * very first render read the cache synchronously and paint.
 *
 * If it is wrong, which only happens on the first open after switching
 * profiles, the real fetch replaces it a moment later.
 */
const LAST = 'cc:cards:last-profile';

export function readLastProfile() {
  try {
    return localStorage.getItem(LAST) || null;
  } catch {
    return null;
  }
}

export function writeLastProfile(profileId) {
  try {
    if (profileId) localStorage.setItem(LAST, profileId);
  } catch {
    /* the app works without it */
  }
}

/** Everything the first render needs, read synchronously. */
export function readLastCards() {
  const id = readLastProfile();
  return id ? readCards(id) : null;
}

export default {
  readCards, readMeta, writeCards, clearCards,
  readLastCards, readLastProfile, writeLastProfile, FIRST_PAGE,
};
